import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { EMPTY, Observable, expand, map, reduce, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  BudgetLineIncludeOption,
  CrmTag,
  ColumnValuesPayload,
  ColumnValuesResponse,
  GetAdvancedParams,
  GetParams,
  GetResponse,
  PostResponse,
  SaleOrderLineIncludeOption,
  SaleOrderLineOptions,
} from '@app/shared';
import { CompanyService, convertGetParamsToHttpParams } from '@app/core';
import {
  SaleOrderPdfVersion,
  SaleOrderPdfVersionResponse,
} from '../../shared/models/sale-order-pdf-version';
import { LossReason } from '../../shared/models/loss-reason';
import { SalesTeam } from '../../shared/models/sales-team';
import { CostCenter } from '../../shared/models/cost-center';

@Injectable({
  providedIn: 'root',
})
export class OpportunityService {
  private readonly saleOrderLinesPageSize = 100;
  private opportunityAdvancedUrl =
    environment.coreApiBaseUrl + '/api/opportunity/get/advanced';
  private opportunityUrl = environment.coreApiBaseUrl + '/api/opportunity';
  private leadAdvancedUrl =
    environment.coreApiBaseUrl + '/api/lead/get/advanced';
  private leadUrl = environment.coreApiBaseUrl + '/api/lead';
  private costCenterUrl = environment.coreApiBaseUrl + '/api/costcenter';
  private crmTagUrl = environment.coreApiBaseUrl + '/api/crmtag';
  private crmStageUrl = environment.coreApiBaseUrl + '/api/crmstage';
  private salesTeamUrl = environment.coreApiBaseUrl + '/api/salesteam';
  private saleOrderUrl = environment.coreApiBaseUrl + '/api/saleorder';
  private saleOrderLineUrl = environment.coreApiBaseUrl + '/api/saleorderline';
  private saleOrderLineAdvancedUrl =
    environment.coreApiBaseUrl + '/api/saleorderline/get/advanced';
  private saleOrderLineTaxRelUrl =
    environment.coreApiBaseUrl + '/api/saleorderlinetaxrel';
  private budgetLineUrl = environment.coreApiBaseUrl + '/api/budgetline';
  private budgetLineAdvancedUrl =
    environment.coreApiBaseUrl + '/api/budgetline/get/advanced';
  private budgetLineTaxRelUrl =
    environment.coreApiBaseUrl + '/api/budgetlinetaxrel';
  private lossReasonUrl = environment.coreApiBaseUrl + '/api/lostreason';
  private currencyUrl = environment.financeApiBaseUrl + '/api/currency';
  private saleOrderPdfUrl = environment.financeApiBaseUrl + '/api/saleOrderPdf';

  constructor(
    private http: HttpClient,
    private companyService: CompanyService
  ) { }

  private buildCompanyScopedHeaders(
    companyId?: number,
    accessToken?: string
  ): HttpHeaders {
    let headers =
      companyId === undefined
        ? this.companyService.getCompanyHeaders()
        : new HttpHeaders({
            accept: 'application/json',
            'Content-Type': 'application/json',
          });

    if (companyId !== undefined && Number.isInteger(companyId) && companyId > 0) {
      headers = headers.set('companyId', companyId.toString());
    }

    if (accessToken) {
      headers = headers.set('Authorization', `Bearer ${accessToken}`);
    }

    return headers;
  }

  getAdvancedOpportunity(
    getParams: GetAdvancedParams,
    includeMetrics: boolean = false,
    companyIds?: number[]
  ): Observable<GetResponse> {
    const headers = this.companyService.getCompanyHeaders();

    // Agregar companyId al payload: usar override si se proporciona, sino selecciÃ³n mÃºltiple, sino la compaÃ±Ã­a Ãºnica
    const multiIds = companyIds || this.companyService.selectedCompanyIds$.value;
    const payload = {
      ...getParams,
      companyId: multiIds.length > 0
        ? multiIds
        : [this.companyService.selectedCompanyId$.value || 0],
    };

    // Crear HttpParams para includeMetrics si es necesario
    let params = new HttpParams();
    if (includeMetrics) {
      params = params.set('IncludeMetrics', 'true');
    }

    return this.http.post<GetResponse>(this.opportunityAdvancedUrl, payload, {
      headers,
      params,
    });
  }

  getOpportunityColumnValues<TColumn extends string = string>(
    payload: ColumnValuesPayload<TColumn>
  ): Observable<ColumnValuesResponse<TColumn>> {
    const headers = this.companyService.getCompanyHeaders();
    return this.http.post<ColumnValuesResponse<TColumn>>(
      `${this.opportunityUrl}/column-values`,
      payload,
      {headers}
    );
  }

  createOpportunity(opportunity: any): Observable<PostResponse> {
    const headers = this.companyService.getCompanyHeaders();
    return this.http.post<PostResponse>(this.opportunityUrl, opportunity, {
      headers,
    });
  }

  updateOpportunity(
    opportunityId: number,
    opportunity: any
  ): Observable<PostResponse> {
    const headers = this.companyService.getCompanyHeaders();
    // El ID va en el payload, no en la ruta
    const payload = {
      ...opportunity,
      id: opportunityId,
    };
    return this.http.put<PostResponse>(this.opportunityUrl, payload, {
      headers,
    });
  }

  patchOpportunity(patchPayload: {
    id: number;
    [key: string]: any;
  }): Observable<PostResponse> {
    const headers = this.companyService.getCompanyHeaders();
    return this.http.put<PostResponse>(this.opportunityUrl, patchPayload, {
      headers,
    });
  }

  getOpportunityBudgetPdfData(
    opportunityId: number,
    accessToken?: string
  ): Observable<GetResponse> {
    const companyId = this.companyService.selectedCompanyId$.getValue();
    let headers = new HttpHeaders({
      accept: 'application/json',
      'Content-Type': 'application/json',
    });

    if (companyId) {
      headers = headers.set('companyId', companyId.toString());
    }

    if (accessToken) {
      headers = headers.set('Authorization', `Bearer ${accessToken}`);
    }

    return this.http.get<GetResponse>(
      `${this.opportunityUrl}/${opportunityId}/budget-pdf-data`,
      { headers }
    );
  }

  getOpportunityById(
    opportunityId?: number,
    getParams?: GetParams,
    accessToken?: string,
    includeMetrics: boolean = false
  ): Observable<GetResponse> {
    const companyId = this.companyService.selectedCompanyId$.getValue();
    let headers = new HttpHeaders({
      accept: 'application/json',
      'Content-Type': 'application/json',
    });

    if (companyId) {
      headers = headers.set('companyId', companyId.toString());
    }

    // Si el accessToken es proporcionado, agregamos el header Authorization
    if (accessToken) {
      headers = headers.set('Authorization', `Bearer ${accessToken}`);
    }

    // Si se proporciona getParams, usarlos directamente (sin filtrado por ID)
    if (getParams) {
      let params = convertGetParamsToHttpParams(getParams);
      if (includeMetrics) {
        params = params.set('IncludeMetrics', 'true');
      }
      return this.http.get<GetResponse>(this.opportunityUrl, {
        headers,
        params,
      });
    }

    // Si se proporciona opportunityId, usar filtrado por ID
    if (opportunityId !== undefined && opportunityId !== null) {
      let params = convertGetParamsToHttpParams({
        startRow: 0,
        endRow: 1,
        sortDirection: 'asc',
        sortProperty: 'id',
        filterProperty: 'id',
        filterValue: opportunityId.toString(),
      });
      if (includeMetrics) {
        params = params.set('IncludeMetrics', 'true');
      }
      return this.http.get<GetResponse>(this.opportunityUrl, {
        headers,
        params,
      });
    }

    // Si no se proporciona ni getParams ni opportunityId, usar valores por defecto sin filtrado
    let params = convertGetParamsToHttpParams({
      startRow: 0,
      endRow: 1000,
      sortDirection: 'desc',
      sortProperty: 'id',
    });
    if (includeMetrics) {
      params = params.set('IncludeMetrics', 'true');
    }
    return this.http.get<GetResponse>(this.opportunityUrl, {
      headers,
      params,
    });
  }

  deleteOpportunity(opportunityIds: number[]): Observable<any> {
    const headers = this.companyService.getCompanyHeaders();
    return this.http.delete(this.opportunityUrl, {
      headers,
      body: { ids: opportunityIds },
    });
  }

  deleteLead(leadIds: number[]): Observable<any> {
    const headers = this.companyService.getCompanyHeaders();
    return this.http.delete(this.leadUrl, {
      headers,
      body: { ids: leadIds },
    });
  }

  unlinkProject(opportunityId: number, projectId: number): Observable<PostResponse> {
    const headers = this.companyService.getCompanyHeaders();
    return this.http.post<PostResponse>(
      `${this.opportunityUrl}/unlink-project`,
      { opportunityId, projectId },
      { headers }
    );
  }

  linkProject(opportunityId: number, projectId: number): Observable<PostResponse> {
    const headers = this.companyService.getCompanyHeaders();
    return this.http.post<PostResponse>(
      `${this.opportunityUrl}/link-project`,
      { opportunityId, projectId },
      { headers }
    );
  }

  getCostCenter(getParams: GetParams): Observable<GetResponse> {
    const params = convertGetParamsToHttpParams(getParams);

    const companyId = this.companyService.selectedCompanyId$.getValue();
    let headers = new HttpHeaders({
      accept: 'application/json',
      'Content-Type': 'application/json',
    });

    if (companyId) {
      headers = headers.set('companyId', companyId.toString());
    }

    return this.http.get<GetResponse>(this.costCenterUrl, {
      params,
      headers,
    });
  }
  createCostCenter(costCenter: CostCenter): Observable<PostResponse> {
    const headers = this.companyService.getCompanyHeaders();
    return this.http.post<PostResponse>(this.costCenterUrl, costCenter, {
      headers,
    });
  }
  updateCostCenter(costCenter: CostCenter): Observable<any> {
    const headers = this.companyService.getCompanyHeaders();
    return this.http.put(this.costCenterUrl, costCenter, { headers });
  }
  deleteCostCenter(ids: number[]): Observable<PostResponse> {
    return this.http.delete<PostResponse>(this.costCenterUrl, {
      body: { ids },
    });
  }

  getCrmTag(getParams: GetParams): Observable<GetResponse> {
    const params = convertGetParamsToHttpParams(getParams);

    const companyId = this.companyService.selectedCompanyId$.getValue();
    let headers = new HttpHeaders({
      accept: 'application/json',
      'Content-Type': 'application/json',
    });

    if (companyId) {
      headers = headers.set('companyId', companyId.toString());
    }

    return this.http.get<GetResponse>(this.crmTagUrl, {
      params,
      headers,
    });
  }
  createCrmTag(tag: CrmTag): Observable<PostResponse> {
    const headers = this.companyService.getCompanyHeaders();
    return this.http.post<PostResponse>(this.crmTagUrl, tag, { headers });
  }
  updateCrmTag(tag: CrmTag): Observable<any> {
    const headers = this.companyService.getCompanyHeaders();
    return this.http.put(this.crmTagUrl, tag, { headers });
  }
  deleteCrmTag(ids: number[]): Observable<PostResponse> {
    return this.http.delete<PostResponse>(this.crmTagUrl, { body: { ids } });
  }
  getLossReason(getParams: GetParams): Observable<GetResponse> {
    const params = convertGetParamsToHttpParams(getParams);

    const companyId = this.companyService.selectedCompanyId$.getValue();
    let headers = new HttpHeaders({
      accept: 'application/json',
      'Content-Type': 'application/json',
    });

    if (companyId) {
      headers = headers.set('companyId', companyId.toString());
    }

    return this.http.get<GetResponse>(this.lossReasonUrl, {
      params,
      headers,
    });
  }
  createLossReason(lossReason: LossReason): Observable<PostResponse> {
    const headers = this.companyService.getCompanyHeaders();
    return this.http.post<PostResponse>(this.lossReasonUrl, lossReason, {
      headers,
    });
  }
  updateLossReason(lossReason: LossReason): Observable<any> {
    const headers = this.companyService.getCompanyHeaders();
    return this.http.put(this.lossReasonUrl, lossReason, { headers });
  }
  deleteLossReason(ids: number[]): Observable<PostResponse> {
    return this.http.delete<PostResponse>(this.lossReasonUrl, {
      body: { ids },
    });
  }

  getCrmStage(getParams: GetParams): Observable<GetResponse> {
    const params = convertGetParamsToHttpParams(getParams);

    const companyId = this.companyService.selectedCompanyId$.getValue();
    let headers = new HttpHeaders({
      accept: 'application/json',
      'Content-Type': 'application/json',
    });

    if (companyId) {
      headers = headers.set('companyId', companyId.toString());
    }

    return this.http.get<GetResponse>(this.crmStageUrl, {
      params,
      headers,
    });
  }

  getAdvancedCrmStage(getParams: GetAdvancedParams): Observable<GetResponse> {
    const headers = this.companyService.getCompanyHeaders();

    // Agregar companyId al payload: usar selecciÃ³n mÃºltiple si existe, sino la compaÃ±Ã­a Ãºnica
    const multiIds = this.companyService.selectedCompanyIds$.value;
    const payload = {
      ...getParams,
      companyId: multiIds.length > 0
        ? multiIds
        : [this.companyService.selectedCompanyId$.value || 0],
    };

    return this.http.post<GetResponse>(
      `${this.crmStageUrl}/get/advanced`,
      payload,
      { headers }
    );
  }

  getSalesTeam(
    getParams: GetParams,
    accessToken?: string
  ): Observable<GetResponse> {
    const params = convertGetParamsToHttpParams(getParams);

    const companyId = this.companyService.selectedCompanyId$.getValue();
    let headers = new HttpHeaders({
      accept: 'application/json',
      'Content-Type': 'application/json',
    });

    if (companyId) {
      headers = headers.set('companyId', companyId.toString());
    }

    // Si el accessToken es proporcionado, agregamos el header Authorization
    if (accessToken) {
      headers = headers.set('Authorization', `Bearer ${accessToken}`);
    }

    return this.http.get<GetResponse>(this.salesTeamUrl, {
      params,
      headers,
    });
  }
  createSalesTeam(salesTeam: SalesTeam): Observable<PostResponse> {
    const headers = this.companyService.getCompanyHeaders();
    return this.http.post<PostResponse>(this.salesTeamUrl, salesTeam, {
      headers,
    });
  }
  updateSalesTeam(salesTeam: SalesTeam): Observable<any> {
    const headers = this.companyService.getCompanyHeaders();
    return this.http.put(this.salesTeamUrl, salesTeam, { headers });
  }
  deleteSalesTeam(ids: number[]): Observable<PostResponse> {
    return this.http.delete<PostResponse>(this.salesTeamUrl, { body: { ids } });
  }

  getAdvancedLead(getParams: GetAdvancedParams, includeMetrics: boolean = false, companyIds?: number[]): Observable<GetResponse> {
    const headers = this.companyService.getCompanyHeaders();

    // Agregar companyId al payload: usar override si se proporciona, sino selecciÃ³n mÃºltiple, sino la compaÃ±Ã­a Ãºnica
    const multiIds = companyIds || this.companyService.selectedCompanyIds$.value;
    const payload = {
      ...getParams,
      companyId: multiIds.length > 0
        ? multiIds
        : [this.companyService.selectedCompanyId$.value || 0],
    };

    // Crear HttpParams para includeMetrics si es necesario
    let params = new HttpParams();
    if (includeMetrics) {
      params = params.set('IncludeMetrics', 'true');
    }

    return this.http.post<GetResponse>(this.leadAdvancedUrl, payload, {
      headers,
      params,
    });
  }

  getLeadColumnValues<TColumn extends string = string>(
    payload: ColumnValuesPayload<TColumn>
  ): Observable<ColumnValuesResponse<TColumn>> {
    const headers = this.companyService.getCompanyHeaders();
    return this.http.post<ColumnValuesResponse<TColumn>>(
      `${this.leadUrl}/column-values`,
      payload,
      {headers}
    );
  }

  createLead(lead: any): Observable<PostResponse> {
    const headers = this.companyService.getCompanyHeaders();
    return this.http.post<PostResponse>(this.leadUrl, lead, { headers });
  }

  convertLead(leadId: number, currencyId: number, expectedClosingDate: string | null): Observable<PostResponse> {
    const headers = this.companyService.getCompanyHeaders();
    const requestBody: any = {
      id: leadId,
      currencyId: currencyId,
      expectedClosingDate: expectedClosingDate
    };
    return this.http.post<PostResponse>(
      `${this.leadUrl}/convert`,
      requestBody,
      { headers }
    );
  }

  patchLead(patchPayload: {
    id: number;
    [key: string]: any;
  }): Observable<PostResponse> {
    const headers = this.companyService.getCompanyHeaders();
    return this.http.put<PostResponse>(this.leadUrl, patchPayload, { headers });
  }

  markOpportunityAsWon(opportunityId: number): Observable<PostResponse> {
    const headers = this.companyService.getCompanyHeaders();
    return this.http.post<PostResponse>(
      `${this.opportunityUrl}/won`,
      { leadId: opportunityId },
      { headers }
    );
  }

  /** Sends an awarded opportunity to SourTrack by hand. The outcome is the envelope's messageType, not the HTTP status. */
  syncSourTrack(opportunityId: number): Observable<PostResponse> {
    const headers = this.companyService.getCompanyHeaders();
    return this.http.post<PostResponse>(
      `${this.opportunityUrl}/${opportunityId}/sync-sourtrack`,
      null,
      { headers }
    );
  }

  markAsLost(
    id: number,
    reason?: string,
    lostNote?: string
  ): Observable<PostResponse> {
    const headers = this.companyService.getCompanyHeaders();
    const payload: any = { leadId: id };
    if (reason) payload.reason = reason;
    if (lostNote) payload.lostNote = lostNote;
    return this.http.post<PostResponse>(`${this.leadUrl}/lost`, payload, {
      headers,
    });
  }

  duplicateLead(leadId: number): Observable<PostResponse> {
    const headers = this.companyService.getCompanyHeaders();
    return this.http.post<PostResponse>(
      `${this.leadUrl}/duplicate`,
      { id: leadId },
      { headers }
    );
  }

  duplicateOpportunity(opportunityId: number): Observable<PostResponse> {
    const headers = this.companyService.getCompanyHeaders();
    return this.http.post<PostResponse>(
      `${this.opportunityUrl}/duplicate`,
      { id: opportunityId },
      { headers }
    );
  }

  recoverLead(id: number): Observable<PostResponse> {
    const headers = this.companyService.getCompanyHeaders();
    return this.http.post<PostResponse>(
      `${this.leadUrl}/recover`,
      { leadId: id },
      { headers }
    );
  }

  addLeadTag(leadId: number, tagId: number): Observable<PostResponse> {
    const headers = this.companyService.getCompanyHeaders();
    const leadtagUrl = environment.coreApiBaseUrl + '/api/leadtag';
    return this.http.post<PostResponse>(
      leadtagUrl,
      { leadId, tagId },
      { headers }
    );
  }

  removeLeadTag(leadId: number, tagId: number): Observable<PostResponse> {
    const headers = this.companyService.getCompanyHeaders();
    const leadtagUrl = environment.coreApiBaseUrl + '/api/leadtag';
    return this.http.delete<PostResponse>(leadtagUrl, {
      headers,
      body: { leadId, tagId },
    });
  }

  getSaleOrders(
    getParams: GetParams & { leadId?: number },
    accessToken?: string,
    companyId?: number
  ): Observable<GetResponse> {
    let params = convertGetParamsToHttpParams(getParams);
    if (getParams.leadId !== undefined) {
      params = params.set('leadId', getParams.leadId.toString());
    }

    const headers = this.buildCompanyScopedHeaders(companyId, accessToken);

    return this.http.get<GetResponse>(this.saleOrderUrl, {
      params,
      headers,
    });
  }

  getAdvancedSaleOrder(
    getParams: GetAdvancedParams & { leadId?: number | null },
    companyIds?: number[]
  ): Observable<GetResponse> {
    const headers = this.companyService.getCompanyHeaders();

    // Agregar companyId al payload: usar selecciÃ³n mÃºltiple si existe, sino la compaÃ±Ã­a Ãºnica
    const multiIds = companyIds || this.companyService.selectedCompanyIds$.value;
    const payload: any = {
      ...getParams,
      leadId: getParams.leadId !== undefined ? getParams.leadId : null,
      companyId: multiIds.length > 0
        ? multiIds
        : [this.companyService.selectedCompanyId$.value || 0],
    };

    return this.http.post<GetResponse>(
      `${this.saleOrderUrl}/get/advanced`,
      payload,
      { headers }
    );
  }

  getSaleOrderColumnValues<TColumn extends string = string>(
    payload: ColumnValuesPayload<TColumn>
  ): Observable<ColumnValuesResponse<TColumn>> {
    const headers = this.companyService.getCompanyHeaders();
    return this.http.post<ColumnValuesResponse<TColumn>>(
      `${this.saleOrderUrl}/column-values`,
      payload,
      {headers}
    );
  }

  createSaleOrder(saleOrder: any): Observable<PostResponse> {
    const headers = this.companyService.getCompanyHeaders();
    return this.http.post<PostResponse>(this.saleOrderUrl, saleOrder, {
      headers,
    });
  }
  updateSaleOrder(
    saleOrderId: number,
    saleOrder: any
  ): Observable<PostResponse> {
    const headers = this.companyService.getCompanyHeaders();
    // El ID va en el payload, no en la ruta
    const payload = {
      ...saleOrder,
      id: saleOrderId,
    };
    return this.http.put<PostResponse>(this.saleOrderUrl, payload, {
      headers,
    });
  }
  getSaleOrderById(saleOrderId: number): Observable<any> {
    const headers = this.companyService.getCompanyHeaders();
    return this.http.get<any>(`${this.saleOrderUrl}/${saleOrderId}`, {
      headers,
    });
  }

  getSaleOrderLineOptions(saleOrderId: number): Observable<{ data: SaleOrderLineOptions }> {
    const headers = this.companyService.getCompanyHeaders();
    return this.http.get<{ data: SaleOrderLineOptions }>(
      `${this.saleOrderUrl}/${saleOrderId}/line-options`,
      { headers }
    );
  }

  // Budget Line methods
  getBudgetLines(
    saleOrderId: number,
    getParams?: GetParams
  ): Observable<GetResponse> {
    const defaultParams: GetParams = {
      startRow: 0,
      endRow: 100,
      sortDirection: 'asc',
      sortProperty: 'id',
    };
    let params = convertGetParamsToHttpParams(getParams || defaultParams);
    params = params.set('saleOrderId', saleOrderId.toString());
    const headers = this.companyService.getCompanyHeaders();
    return this.http.get<GetResponse>(this.budgetLineUrl, {
      params,
      headers,
    });
  }

  getAdvancedBudgetLines(
    saleOrderId: number,
    getParams?: Partial<GetAdvancedParams> & { includes?: BudgetLineIncludeOption[] }
  ): Observable<GetResponse> {
    const headers = this.companyService.getCompanyHeaders();
    const payload: GetAdvancedParams & { saleOrderId: number } = {
      startRow: 0,
      endRow: 100,
      sortDirection: 'asc',
      sortProperty: 'id',
      ...getParams,
      saleOrderId,
      filterGroups: getParams?.filterGroups || [],
    };

    return this.http.post<GetResponse>(this.budgetLineAdvancedUrl, payload, {
      headers,
    });
  }

  getBudgetLineColumnValues<TColumn extends string = string>(
    payload: ColumnValuesPayload<TColumn>
  ): Observable<ColumnValuesResponse<TColumn>> {
    const headers = this.companyService.getCompanyHeaders();
    return this.http.post<ColumnValuesResponse<TColumn>>(
      `${this.budgetLineUrl}/column-values`,
      payload,
      {headers}
    );
  }

  createBudgetLine(budgetLine: any): Observable<PostResponse> {
    const headers = this.companyService.getCompanyHeaders();
    return this.http.post<PostResponse>(this.budgetLineUrl, budgetLine, {
      headers,
    });
  }

  updateBudgetLine(
    budgetLineId: number,
    budgetLine: any
  ): Observable<PostResponse> {
    const headers = this.companyService.getCompanyHeaders();
    const payload = {
      ...budgetLine,
      id: budgetLineId,
    };
    return this.http.put<PostResponse>(this.budgetLineUrl, payload, {
      headers,
    });
  }

  deleteBudgetLine(saleOrderId: number, ids: number[]): Observable<any> {
    const headers = this.companyService.getCompanyHeaders();
    const payload = {
      saleOrderId: saleOrderId,
      ids: ids,
    };
    return this.http.delete<any>(`${this.budgetLineUrl}`, {
      headers,
      body: payload,
    });
  }

  changeOpportunityStage(
    opportunityId: number,
    stageId: number
  ): Observable<PostResponse> {
    const headers = this.companyService.getCompanyHeaders();
    return this.http.post<PostResponse>(
      `${this.opportunityUrl}/changestage`,
      { opportunityId, stageId },
      { headers }
    );
  }

  changeLeadStage(leadId: number, stageId: number): Observable<PostResponse> {
    const headers = this.companyService.getCompanyHeaders();
    return this.http.post<PostResponse>(
      `${this.leadUrl}/changestage`,
      { leadId, stageId },
      { headers }
    );
  }

  // Sale Order actions
  cancelSaleOrder(
    saleOrderId: number,
    leadId: number
  ): Observable<PostResponse> {
    const headers = this.companyService.getCompanyHeaders();
    return this.http.put<PostResponse>(
      `${this.saleOrderUrl}/cancel`,
      { id: saleOrderId, leadId },
      { headers }
    );
  }

  recoverSaleOrder(
    saleOrderId: number,
    leadId: number
  ): Observable<PostResponse> {
    const headers = this.companyService.getCompanyHeaders();
    return this.http.put<PostResponse>(
      `${this.saleOrderUrl}/recover`,
      { id: saleOrderId, leadId },
      { headers }
    );
  }

  setApproveStatusSaleOrder(
    saleOrderId: number,
    leadId: number,
    approveStatus: boolean
  ): Observable<PostResponse> {
    const headers = this.companyService.getCompanyHeaders();
    return this.http.put<PostResponse>(
      `${this.saleOrderUrl}/setapprovestatus`,
      { id: saleOrderId, leadId, approveStatus },
      { headers }
    );
  }
  askForApproveSaleOrder(
    saleOrderId: number,
    leadId: number
  ): Observable<PostResponse> {
    const headers = this.companyService.getCompanyHeaders();
    return this.http.put<PostResponse>(
      `${this.saleOrderUrl}/ask-for-approve
`,
      { id: saleOrderId, leadId },
      { headers }
    );
  }

  confirmSaleOrder(
    saleOrderId: number,
    leadId: number
  ): Observable<PostResponse> {
    const headers = this.companyService.getCompanyHeaders();
    return this.http.put<PostResponse>(
      `${this.saleOrderUrl}/confirm`,
      { id: saleOrderId, leadId },
      { headers }
    );
  }

  changeSaleOrderStage(
    saleOrderId: number,
    leadId: number,
    stage: string
  ): Observable<PostResponse> {
    const headers = this.companyService.getCompanyHeaders();
    return this.http.put<PostResponse>(
      `${this.saleOrderUrl}/changestage`,
      { id: saleOrderId, leadId, stage },
      { headers }
    );
  }

  adoptSaleOrder(
    saleOrderId: number,
    leadId: number,
    updateLinkedSaleOrderLines?: boolean | null
  ): Observable<PostResponse> {
    const headers = this.companyService.getCompanyHeaders();
    const payload: any = { id: saleOrderId, leadId };
    if (updateLinkedSaleOrderLines !== undefined) {
      payload.updateLinkedSaleOrderLines = updateLinkedSaleOrderLines;
    }

    return this.http.put<PostResponse>(
      `${this.saleOrderUrl}/adopt`,
      payload,
      { headers }
    );
  }

  // Budget Line Tax methods
  addBudgetLineTax(
    budgetLineId: number,
    taxId: number
  ): Observable<PostResponse> {
    const headers = this.companyService.getCompanyHeaders();
    return this.http.post<PostResponse>(
      this.budgetLineTaxRelUrl,
      { budgetLineId, taxId },
      { headers }
    );
  }

  getBudgetLineTaxes(
    budgetLineIds: number | number[],
    getParams?: GetParams
  ): Observable<GetResponse> {
    const defaultParams: GetParams = {
      startRow: 0,
      endRow: 1000,
      sortDirection: 'asc',
      sortProperty: 'id',
    };
    let params = convertGetParamsToHttpParams(getParams || defaultParams);
    // Si es un array, agregar mÃºltiples parÃ¡metros budgetLineId
    if (Array.isArray(budgetLineIds)) {
      budgetLineIds.forEach((id) => {
        params = params.append('budgetLineId', id.toString());
      });
    } else {
      params = params.set('budgetLineId', budgetLineIds.toString());
    }
    const headers = this.companyService.getCompanyHeaders();
    return this.http.get<GetResponse>(this.budgetLineTaxRelUrl, {
      params,
      headers,
    });
  }

  deleteBudgetLineTax(budgetLineId: number, taxIds: number[]): Observable<any> {
    const headers = this.companyService.getCompanyHeaders();
    return this.http.delete<any>(this.budgetLineTaxRelUrl, {
      headers,
      body: { budgetLineId, ids: taxIds },
    });
  }

  // Sale Order Line methods
  getSaleOrderLines(
    saleOrderId: number,
    getParams?: GetParams,
    accessToken?: string
  ): Observable<GetResponse> {
    const defaultParams: GetParams = {
      startRow: 0,
      endRow: 100,
      sortDirection: 'asc',
      sortProperty: 'id',
    };
    let params = convertGetParamsToHttpParams(getParams || defaultParams);
    params = params.set('saleOrderId', saleOrderId.toString());

    const companyId = this.companyService.selectedCompanyId$.getValue();
    let headers = new HttpHeaders({
      accept: 'application/json',
      'Content-Type': 'application/json',
    });

    if (companyId) {
      headers = headers.set('companyId', companyId.toString());
    }

    // Si el accessToken es proporcionado, agregamos el header Authorization
    if (accessToken) {
      headers = headers.set('Authorization', `Bearer ${accessToken}`);
    }

    return this.http.get<GetResponse>(this.saleOrderLineUrl, {
      params,
      headers,
    });
  }

  getAllSaleOrderLines(
    saleOrderId: number,
    accessToken?: string
  ): Observable<GetResponse> {
    const pageSize = this.saleOrderLinesPageSize;
    const getPage = (startRow: number): Observable<GetResponse> => this.getSaleOrderLines(
      saleOrderId,
      {
        startRow,
        endRow: startRow + pageSize,
        sortDirection: 'asc',
        sortProperty: 'id',
      },
      accessToken
    );

    return getPage(0).pipe(
      expand((response, pageIndex) => {
        const rows = response?.data?.rows;
        const lastRow = response?.data?.lastRow;
        const startRow = pageIndex * pageSize;
        const endRow = startRow + pageSize;

        if (!Array.isArray(rows) || !Number.isInteger(lastRow) || lastRow < 0) {
          return throwError(() => this.saleOrderLinesIntegrityError('respuesta paginada inválida.'));
        }

        if (rows.length === 0 && startRow < lastRow) {
          return throwError(() => this.saleOrderLinesIntegrityError(
            `página vacía en el rango [${startRow}, ${endRow}) (${startRow}/${lastRow}).`
          ));
        }

        const expectedPageLength = Math.min(pageSize, Math.max(lastRow - startRow, 0));
        if (rows.length < expectedPageLength) {
          return throwError(() => this.saleOrderLinesIntegrityError(
            `respuesta corta en el rango [${startRow}, ${endRow}) (${rows.length}/${expectedPageLength}).`
          ));
        }

        return endRow < lastRow ? getPage(endRow) : EMPTY;
      }),
      reduce((accumulator, response) => {
        const lastRow = response.data.lastRow;
        if (accumulator.lastRow !== null && accumulator.lastRow !== lastRow) {
          throw this.saleOrderLinesIntegrityError(
            `el total cambió durante la carga (${accumulator.lastRow}/${lastRow}).`
          );
        }

        for (const row of response.data.rows) {
          if (!Number.isInteger(row?.id) || row.id <= 0) {
            throw this.saleOrderLinesIntegrityError('línea sin identificador válido.');
          }

          if (accumulator.ids.has(row.id)) {
            throw this.saleOrderLinesIntegrityError(`identificador duplicado ${row.id}.`);
          }

          accumulator.ids.add(row.id);
          accumulator.rows.push(row);
        }

        accumulator.lastRow = lastRow;
        accumulator.statusCode = response.statusCode;
        accumulator.message = response.message;
        return accumulator;
      }, {
        rows: [] as any[],
        ids: new Set<unknown>(),
        lastRow: null as number | null,
        statusCode: 200 as GetResponse['statusCode'],
        message: 'Ok',
      }),
      map((result): GetResponse => {
        const lastRow = result.lastRow ?? 0;
        if (result.rows.length !== lastRow) {
          throw this.saleOrderLinesIntegrityError(
            `conteo incompleto (${result.rows.length}/${lastRow}).`
          );
        }

        return {
          data: { rows: result.rows, lastRow },
          statusCode: result.statusCode,
          message: result.message,
        };
      })
    );
  }

  private saleOrderLinesIntegrityError(reason: string): Error {
    return new Error(`No se pudieron recuperar todas las líneas del presupuesto: ${reason}`);
  }

  getAdvancedSaleOrderLines(
    saleOrderId: number,
    getParams?: Partial<GetAdvancedParams> & { includes?: SaleOrderLineIncludeOption[] }
  ): Observable<GetResponse> {
    const headers = this.companyService.getCompanyHeaders();
    const payload: GetAdvancedParams & { saleOrderId: number } = {
      startRow: 0,
      endRow: 100,
      sortDirection: 'asc',
      sortProperty: 'id',
      ...getParams,
      saleOrderId,
      filterGroups: getParams?.filterGroups || [],
    };

    return this.http.post<GetResponse>(this.saleOrderLineAdvancedUrl, payload, {
      headers,
    });
  }

  getSaleOrderLineColumnValues<TColumn extends string = string>(
    payload: ColumnValuesPayload<TColumn>
  ): Observable<ColumnValuesResponse<TColumn>> {
    const headers = this.companyService.getCompanyHeaders();
    return this.http.post<ColumnValuesResponse<TColumn>>(
      `${this.saleOrderLineUrl}/column-values`,
      payload,
      {headers}
    );
  }

  createSaleOrderLine(saleOrderLine: any): Observable<PostResponse> {
    const headers = this.companyService.getCompanyHeaders();
    return this.http.post<PostResponse>(this.saleOrderLineUrl, saleOrderLine, {
      headers,
    });
  }

  updateSaleOrderLine(
    saleOrderLineId: number,
    saleOrderLine: any
  ): Observable<PostResponse> {
    const headers = this.companyService.getCompanyHeaders();
    const payload = {
      ...saleOrderLine,
      id: saleOrderLineId,
    };
    return this.http.put<PostResponse>(this.saleOrderLineUrl, payload, {
      headers,
    });
  }

  deleteSaleOrderLine(saleOrderId: number, ids: number[]): Observable<any> {
    const headers = this.companyService.getCompanyHeaders();
    const payload = {
      saleOrderId: saleOrderId,
      ids: ids,
    };
    return this.http.delete<any>(`${this.saleOrderLineUrl}`, {
      headers,
      body: payload,
    });
  }

  deleteSaleOrder(leadId: number, ids: number[]): Observable<any> {
    const headers = this.companyService.getCompanyHeaders();
    const payload = {
      leadId: leadId,
      ids: ids,
    };
    return this.http.delete<any>(`${this.saleOrderUrl}`, {
      headers,
      body: payload,
    });
  }

  // Sale Order Line Tax methods
  addSaleOrderLineTax(
    saleOrderLineId: number,
    taxId: number
  ): Observable<PostResponse> {
    const headers = this.companyService.getCompanyHeaders();
    return this.http.post<PostResponse>(
      this.saleOrderLineTaxRelUrl,
      { saleOrderLineId, taxId },
      { headers }
    );
  }

  getSaleOrderLineTaxes(
    saleOrderLineIds: number | number[],
    getParams?: GetParams
  ): Observable<GetResponse> {
    const defaultParams: GetParams = {
      startRow: 0,
      endRow: 1000,
      sortDirection: 'asc',
      sortProperty: 'id',
    };
    let params = convertGetParamsToHttpParams(getParams || defaultParams);
    // Si es un array, agregar mÃºltiples parÃ¡metros saleOrderLineId
    if (Array.isArray(saleOrderLineIds)) {
      saleOrderLineIds.forEach((id) => {
        params = params.append('saleOrderLineId', id.toString());
      });
    } else {
      params = params.set('saleOrderLineId', saleOrderLineIds.toString());
    }
    const headers = this.companyService.getCompanyHeaders();
    return this.http.get<GetResponse>(this.saleOrderLineTaxRelUrl, {
      params,
      headers,
    });
  }

  deleteSaleOrderLineTax(
    saleOrderLineId: number,
    taxIds: number[]
  ): Observable<any> {
    const headers = this.companyService.getCompanyHeaders();
    return this.http.delete<any>(this.saleOrderLineTaxRelUrl, {
      headers,
      body: { saleOrderLineId, ids: taxIds },
    });
  }

  // Sale Order PDF methods
  generateSaleOrderPdfPreview(
    saleOrderId: number,
    unitary: boolean = false,
    companyId?: number
  ): Observable<Blob> {
    const headers = this.buildCompanyScopedHeaders(companyId);
    let params = new HttpParams().set('saleOrderId', saleOrderId.toString());
    if (unitary) {
      params = params.set('unitary', 'true');
    }
    return this.http.get(`${this.saleOrderPdfUrl}/preview`, {
      headers,
      params,
      responseType: 'blob',
    });
  }

  downloadSaleOrderPdf(
    saleOrderId: number,
    companyId?: number
  ): Observable<Blob> {
    const headers = this.buildCompanyScopedHeaders(companyId);
    const params = new HttpParams().set('saleOrderId', saleOrderId.toString());
    return this.http.get(`${this.saleOrderPdfUrl}/file`, {
      headers,
      params,
      responseType: 'blob',
    });
  }

  generateSaleOrderPdfVersion(
    saleOrderId: number,
    companyId?: number,
    issueCurrencyId?: number | null
  ): Observable<SaleOrderPdfVersionResponse<SaleOrderPdfVersion>> {
    const headers = this.buildCompanyScopedHeaders(companyId);
    return this.http.post<SaleOrderPdfVersionResponse<SaleOrderPdfVersion>>(
      `${this.saleOrderPdfUrl}/versions`,
      issueCurrencyId ? { saleOrderId, issueCurrencyId } : { saleOrderId },
      { headers }
    );
  }

  getSaleOrderPdfVersions(
    saleOrderId: number,
    companyId?: number
  ): Observable<SaleOrderPdfVersionResponse<SaleOrderPdfVersion[]>> {
    const headers = this.buildCompanyScopedHeaders(companyId);
    const params = new HttpParams().set('saleOrderId', saleOrderId.toString());
    return this.http.get<SaleOrderPdfVersionResponse<SaleOrderPdfVersion[]>>(
      `${this.saleOrderPdfUrl}/versions`,
      { headers, params }
    );
  }

  downloadSaleOrderPdfVersion(
    versionId: number,
    companyId?: number
  ): Observable<Blob> {
    const headers = this.buildCompanyScopedHeaders(companyId);
    return this.http.get(`${this.saleOrderPdfUrl}/versions/${versionId}/file`, {
      headers,
      responseType: 'blob',
    });
  }

  /** Archives a version: it leaves the versions list but keeps its number and its file. */
  archiveSaleOrderPdfVersion(
    versionId: number,
    companyId?: number
  ): Observable<SaleOrderPdfVersionResponse<SaleOrderPdfVersion>> {
    const headers = this.buildCompanyScopedHeaders(companyId);
    return this.http.post<SaleOrderPdfVersionResponse<SaleOrderPdfVersion>>(
      `${this.saleOrderPdfUrl}/versions/${versionId}/archive`,
      {},
      { headers }
    );
  }

  /** Restores an archived version with its original number. No UI uses it yet. */
  restoreSaleOrderPdfVersion(
    versionId: number,
    companyId?: number
  ): Observable<SaleOrderPdfVersionResponse<SaleOrderPdfVersion>> {
    const headers = this.buildCompanyScopedHeaders(companyId);
    return this.http.post<SaleOrderPdfVersionResponse<SaleOrderPdfVersion>>(
      `${this.saleOrderPdfUrl}/versions/${versionId}/restore`,
      {},
      { headers }
    );
  }

  getEmailTemplate(saleOrderId: number | string): Observable<PostResponse> {
    const headers = this.companyService.getCompanyHeaders();
    return this.http.post<PostResponse>(
      `${this.saleOrderUrl}/get-email-template`,
      { saleOrderId },
      { headers }
    );
  }
}
